import { useState, useEffect, useCallback, useMemo } from 'react';
import { createCustomerETLApi } from '../api/etlServiceApi';
import { convertTSqlToDuckDB } from '../utils/convertTSqlToDuckDB';

function rowsToCsv(columns, rows) {
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    return s.includes(';') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const header = columns.map(escape).join(';');
  const body = rows.map(row => columns.map(col => escape(row[col])).join(';')).join('\n');
  return `${header}\n${body}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadCsv(csv, filename) {
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

function ResultsTable({ columns, rows, rowCount, executionTimeMs }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-600">
          {rowCount.toLocaleString()} row{rowCount !== 1 ? 's' : ''}
          {columns.length > 0 && ` · ${columns.length} col${columns.length !== 1 ? 's' : ''}`}
        </span>
        {executionTimeMs != null && (
          <span className="text-xs text-gray-400">{executionTimeMs.toFixed(1)} ms</span>
        )}
      </div>
      <div className="overflow-auto max-h-[360px]">
        <table className="text-xs min-w-full">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              {columns.map(col => (
                <th key={col} className="px-3 py-2 text-left font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {columns.map(col => (
                  <td
                    key={col}
                    className="px-3 py-1.5 text-gray-700 whitespace-nowrap font-mono max-w-[200px] truncate"
                    title={String(row[col] ?? '')}
                  >
                    {row[col] == null ? <span className="text-gray-300 italic">null</span> : String(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function WarehouseBrowser({ customerId, etlServiceReachable }) {
  const api = useMemo(() => (customerId ? createCustomerETLApi(customerId) : null), [customerId]);
  const [schema, setSchema] = useState('erp');
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState(null);

  const [selectedTable, setSelectedTable] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [exportingTable, setExportingTable] = useState(null);
  const [selectedTables, setSelectedTables] = useState(() => new Set());
  const [bulkExporting, setBulkExporting] = useState(false);
  const [bulkExportError, setBulkExportError] = useState(null);

  const [customSql, setCustomSql] = useState('');
  const [showCustomSql, setShowCustomSql] = useState(false);
  const [customResult, setCustomResult] = useState(null);
  const [customRunning, setCustomRunning] = useState(false);
  const [customError, setCustomError] = useState(null);

  const fetchTables = useCallback(async () => {
    if (!etlServiceReachable || !api) return;
    setTablesLoading(true);
    setTablesError(null);
    try {
      const data = await api.listWarehouseTables(schema);
      setTables(data.tables ?? []);
      setSelectedTables(new Set());
    } catch (err) {
      setTablesError(err?.response?.data?.detail ?? err.message ?? 'Failed to load tables');
      setTables([]);
    } finally {
      setTablesLoading(false);
    }
  }, [schema, etlServiceReachable, api]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const previewTable = useCallback(async (tableName) => {
    setSelectedTable(tableName);
    setPreviewResult(null);
    setPreviewError(null);
    setPreviewLoading(true);
    setShowCustomSql(false);
    try {
      const sql = `SELECT * FROM "${schema}"."${tableName}" LIMIT 200`;
      const res = await api.queryWarehouse(sql);
      if (res.success) {
        setPreviewResult(res);
      } else {
        setPreviewError(res.error ?? 'Query failed');
      }
    } catch (err) {
      setPreviewError(err?.response?.data?.detail ?? err.message ?? 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [schema]);

  const exportTable = useCallback(async (tableName, e) => {
    e.stopPropagation();
    if (!api) return;
    setExportingTable(tableName);
    try {
      const res = await api.queryWarehouse(`SELECT * FROM "${schema}"."${tableName}"`);
      if (!res.success) throw new Error(res.error ?? 'Export query failed');
      downloadCsv(rowsToCsv(res.columns, res.rows), `${tableName}.csv`);
    } catch (err) {
      alert(`Export failed: ${err.message}`);
    } finally {
      setExportingTable(null);
    }
  }, [schema, api]);

  const toggleTableSelected = useCallback((tableName, e) => {
    e.stopPropagation();
    setSelectedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) next.delete(tableName);
      else next.add(tableName);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedTables(prev => (prev.size === tables.length ? new Set() : new Set(tables)));
  }, [tables]);

  const runBulkExport = useCallback(async (tablesArg) => {
    if (!api) return;
    setBulkExporting(true);
    setBulkExportError(null);
    try {
      const blob = await api.exportWarehouse(tablesArg, schema);
      const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '_');
      downloadBlob(blob, `warehouse_export_${schema}_${timestamp}.zip`);
    } catch (err) {
      setBulkExportError(err?.response?.data?.detail ?? err.message ?? 'Export failed');
    } finally {
      setBulkExporting(false);
    }
  }, [api, schema]);

  const exportSelected = useCallback(() => runBulkExport(Array.from(selectedTables)), [runBulkExport, selectedTables]);
  const exportAll = useCallback(() => runBulkExport('all'), [runBulkExport]);

  const runCustomSql = useCallback(async () => {
    if (!customSql.trim()) return;

    // Auto-convert T-SQL → DuckDB syntax and update the editor
    const { sql: converted, changed } = convertTSqlToDuckDB(customSql);
    if (changed) setCustomSql(converted);
    const queryToRun = changed ? converted : customSql;

    setCustomRunning(true);
    setCustomError(null);
    setCustomResult(null);
    try {
      const res = await api.queryWarehouse(queryToRun);
      if (res.success) setCustomResult(res);
      else setCustomError(res.error ?? 'Query failed');
    } catch (err) {
      setCustomError(err?.response?.data?.detail ?? err.message ?? 'Request failed');
    } finally {
      setCustomRunning(false);
    }
  }, [customSql, api]);

  if (!etlServiceReachable) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
        <p className="text-sm text-gray-500 font-medium">ETL service is offline</p>
        <p className="text-xs text-gray-400 mt-1">Start the ETL service to browse the warehouse.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header row */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">DuckDB Warehouse</span>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded font-mono">{schema}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowCustomSql(v => !v); setCustomResult(null); setCustomError(null); }}
            className="text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
          >
            {showCustomSql ? 'Hide SQL' : 'Custom SQL'}
          </button>
          <button
            onClick={fetchTables}
            disabled={tablesLoading}
            className="text-xs text-gray-500 hover:text-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <svg className={`w-3.5 h-3.5 ${tablesLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
          </button>
        </div>
      </div>

      {/* Bulk export toolbar */}
      {!tablesLoading && tables.length > 0 && (
        <div className="flex items-center justify-between px-5 py-2 border-b border-gray-200 bg-white">
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={tables.length > 0 && selectedTables.size === tables.length}
              onChange={toggleSelectAll}
              className="rounded border-gray-300"
            />
            {selectedTables.size > 0 ? `${selectedTables.size} selected` : 'Select all'}
          </label>
          <div className="flex items-center gap-2">
            {bulkExportError && <span className="text-xs text-red-600">{bulkExportError}</span>}
            <button
              onClick={exportSelected}
              disabled={bulkExporting || selectedTables.size === 0}
              className="text-xs text-gray-600 hover:text-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {bulkExporting ? 'Exporting…' : `Export Selected (.zip)`}
            </button>
            <button
              onClick={exportAll}
              disabled={bulkExporting}
              className="text-xs text-gray-600 hover:text-gray-800 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {bulkExporting ? 'Exporting…' : 'Export All (.zip)'}
            </button>
          </div>
        </div>
      )}

      <div className="flex min-h-[480px]">
        {/* Tables list sidebar */}
        <div className="w-52 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto">
          {tablesLoading && (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="h-7 bg-gray-200 rounded animate-pulse" />)}
            </div>
          )}
          {tablesError && (
            <div className="p-4">
              <p className="text-xs text-red-600">{tablesError}</p>
            </div>
          )}
          {!tablesLoading && !tablesError && tables.length === 0 && (
            <div className="p-4 text-center">
              <p className="text-xs text-gray-400 font-medium">No tables found</p>
              <p className="text-xs text-gray-400 mt-1">Run the ETL pipeline to populate the warehouse.</p>
            </div>
          )}
          {!tablesLoading && tables.map(table => (
            <div
              key={table}
              className={`group flex items-center border-b border-gray-200/60 transition-colors ${
                selectedTable === table
                  ? 'bg-primary-50'
                  : 'hover:bg-white'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedTables.has(table)}
                onChange={(e) => toggleTableSelected(table, e)}
                onClick={(e) => e.stopPropagation()}
                className="flex-shrink-0 ml-3 rounded border-gray-300"
              />
              <button
                onClick={() => previewTable(table)}
                className={`flex-1 min-w-0 text-left px-3 py-2.5 text-xs font-mono ${
                  selectedTable === table
                    ? 'text-primary-700 font-semibold'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-1.5A1.125 1.125 0 0 1 18 18.375M20.625 4.5H3.375m17.25 0c.621 0 1.125.504 1.125 1.125M20.625 4.5h-1.5C18.504 4.5 18 5.004 18 5.625m3.75 0v1.5c0 .621-.504 1.125-1.125 1.125M3.375 4.5c-.621 0-1.125.504-1.125 1.125M3.375 4.5h1.5C5.496 4.5 6 5.004 6 5.625m-3.75 0v1.5c0 .621.504 1.125 1.125 1.125m0 0h1.5m-1.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m1.5-3.75C5.496 8.25 6 8.754 6 9.375v1.5m0-5.25v5.25m0-5.25C6 5.004 6.504 4.5 7.125 4.5h9.75c.621 0 1.125.504 1.125 1.125m1.125 2.625h1.5m-1.5 0A1.125 1.125 0 0 1 18 9.375v1.5m1.5-3.75C19.496 8.25 20 8.754 20 9.375v1.5m0-5.25v5.25m0-5.25c0-.621-.504-1.125-1.125-1.125H9.375A1.125 1.125 0 0 0 8.25 5.625" />
                  </svg>
                  <span className="truncate">{table}</span>
                </div>
              </button>
              <button
                onClick={(e) => exportTable(table, e)}
                disabled={exportingTable === table}
                title={`Export ${table} as CSV`}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 px-2 py-2 text-gray-400 hover:text-primary-600 disabled:text-gray-300 transition-all"
              >
                {exportingTable === table ? (
                  <span className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin inline-block" />
                ) : (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Main content */}
        <div className="flex-1 p-5 overflow-auto">
          {showCustomSql ? (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Run a custom SELECT query against the DuckDB warehouse.</p>
              <textarea
                value={customSql}
                onChange={e => setCustomSql(e.target.value)}
                onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); runCustomSql(); } }}
                rows={6}
                spellCheck={false}
                className="w-full font-mono text-sm border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y bg-gray-50"
                placeholder={`SELECT * FROM "erp"."YourTable" LIMIT 100`}
              />
              <button
                onClick={runCustomSql}
                disabled={customRunning || !customSql.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {customRunning ? (
                  <><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Running…</>
                ) : 'Run'}
              </button>
              {customError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-red-600 font-mono">{customError}</div>
              )}
              {customResult && customResult.columns.length > 0 && (
                <ResultsTable
                  columns={customResult.columns}
                  rows={customResult.rows}
                  rowCount={customResult.row_count}
                  executionTimeMs={customResult.execution_time_ms}
                />
              )}
            </div>
          ) : !selectedTable ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <svg className="w-10 h-10 text-gray-200 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
              </svg>
              <p className="text-sm text-gray-400 font-medium">Select a table to preview</p>
              <p className="text-xs text-gray-300 mt-1">Click a table on the left to load the first 200 rows.</p>
            </div>
          ) : previewLoading ? (
            <div className="space-y-2">
              <div className="h-8 bg-gray-100 rounded animate-pulse" />
              {[1, 2, 3, 4].map(i => <div key={i} className="h-7 bg-gray-50 rounded animate-pulse" />)}
            </div>
          ) : previewError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <p className="text-xs font-semibold text-red-700 mb-1">Preview Error</p>
              <p className="text-xs text-red-600 font-mono">{previewError}</p>
            </div>
          ) : previewResult ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold text-gray-700 font-mono">{schema}.{selectedTable}</span>
                <span className="text-xs text-gray-400">— first 200 rows</span>
              </div>
              <ResultsTable
                columns={previewResult.columns}
                rows={previewResult.rows}
                rowCount={previewResult.row_count}
                executionTimeMs={previewResult.execution_time_ms}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
